import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { ExternalLink, LogOut } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, fmtErr, SettingsForm, ListEditor } from "@/admin/editors";

const PROFILE_SCHEMA = [
  { key: "name", type: "text", label: "Nama" },
  { key: "role_title", type: "bi", label: "Title Profesional" },
  { key: "headline", type: "bitextarea", label: "Headline", rows: 2 },
  { key: "subheadline", type: "bitextarea", label: "Sub-headline", rows: 4 },
  { key: "availability", type: "bi", label: "Availability Badge" },
  { key: "photo_url", type: "image", label: "Foto Profil" },
];

const SUMMARY_SCHEMA = [
  { key: "summary", type: "bitextarea", label: "Ringkasan Profesional", rows: 8 },
  { key: "quote", type: "bitextarea", label: "Quote / Filosofi", rows: 3 },
];

const CONTACT_SCHEMA = [
  { key: "whatsapp_number", type: "text", label: "Nomor WhatsApp (tampilan)" },
  { key: "whatsapp_link", type: "text", label: "Link WhatsApp (wa.me/…)" },
  { key: "email", type: "text", label: "Email" },
  { key: "instagram", type: "text", label: "Instagram (tanpa @)" },
];

const KPI_SCHEMA = [
  { key: "order", type: "number", label: "Urutan" },
  { key: "value", type: "number", label: "Angka (untuk animasi)" },
  { key: "prefix", type: "text", label: "Prefix (mis. Rp )" },
  { key: "suffix", type: "text", label: "Suffix (mis. x / jt / %)" },
  { key: "decimals", type: "number", label: "Desimal" },
  { key: "label", type: "bi", label: "Label" },
  { key: "description", type: "bitextarea", label: "Keterangan", rows: 3 },
];

const CASE_SCHEMA = [
  { key: "brand", type: "text", label: "Nama Brand / Project" },
  { key: "order", type: "number", label: "Urutan" },
  { key: "industry", type: "bi", label: "Industri" },
  { key: "title", type: "bi", label: "Judul Studi Kasus" },
  { key: "channels", type: "comma", label: "Channel (pisah koma)" },
  { key: "challenge", type: "bitextarea", label: "Objective / Tantangan", rows: 3 },
  { key: "strategy", type: "bitextarea", label: "Strategi", rows: 3 },
  { key: "execution", type: "bitextarea", label: "Eksekusi", rows: 3 },
  { key: "result", type: "bitextarea", label: "Hasil", rows: 3 },
  { key: "metrics", type: "metrics", label: "Metrik" },
  { key: "chart_data", type: "chart", label: "Grafik" },
  { key: "image_url", type: "image", label: "Gambar Bukti" },
];

const EXP_SCHEMA = [
  { key: "order", type: "number", label: "Urutan" },
  { key: "role", type: "bi", label: "Jabatan" },
  { key: "company", type: "text", label: "Perusahaan / Brand" },
  { key: "period", type: "text", label: "Periode" },
  { key: "responsibilities", type: "bitextarea", label: "Tanggung Jawab (pisah dengan ·)", rows: 5 },
  { key: "achievements", type: "bitextarea", label: "Pencapaian Penting", rows: 2 },
];

const SKILL_SCHEMA = [
  { key: "order", type: "number", label: "Urutan" },
  { key: "title", type: "bi", label: "Kategori" },
  { key: "description", type: "bitextarea", label: "Deskripsi", rows: 2 },
  { key: "items", type: "comma", label: "Item (pisah koma)" },
];

const CERT_SCHEMA = [
  { key: "order", type: "number", label: "Urutan" },
  { key: "name", type: "bi", label: "Nama Sertifikasi" },
  { key: "issuer", type: "text", label: "Penerbit" },
  { key: "year", type: "text", label: "Tahun" },
  { key: "credential_url", type: "text", label: "URL Kredensial" },
];

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("Konfirmasi password tidak sama");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/change-password", { current_password: current, new_password: next });
      toast.success("Password diganti");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="admin-password-form">
      <h3 className="text-sm font-bold text-slate-800">Ganti Password</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-600">Password sekarang</Label>
          <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="password-current-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-600">Password baru</Label>
          <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="password-new-input" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs font-semibold text-slate-600">Ulangi password baru</Label>
          <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="password-confirm-input" />
        </div>
      </div>
      <Button type="submit" disabled={busy} data-testid="password-save-button">
        {busy ? "Menyimpan…" : "Ganti Password"}
      </Button>
    </form>
  );
}

const TABS = [
  { value: "profile", label: "Profil & Hero" },
  { value: "summary", label: "Ringkasan" },
  { value: "kpis", label: "KPI" },
  { value: "cases", label: "Studi Kasus" },
  { value: "experience", label: "Pengalaman" },
  { value: "skills", label: "Keahlian" },
  { value: "certs", label: "Sertifikasi" },
  { value: "contact", label: "Kontak & Keamanan" },
];

export default function AdminPanel({ user, onLogout }) {
  const [data, setData] = useState(null);

  const refresh = async () => {
    try {
      const res = await api.get("/content");
      setData(res.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const settings = data?.settings || {};

  return (
    <div className="min-h-screen bg-offwhite" data-testid="admin-panel">
      <Toaster richColors position="bottom-right" />
      <header className="sticky top-0 z-40 border-b border-white/10 bg-navy">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-2.5">
            <img src="/favicon.svg" alt="Logo" className="h-8 w-8 rounded-lg" />
            <div>
              <p className="text-sm font-extrabold text-white">Admin CMS</p>
              <p className="text-[11px] text-slate-400">Halo, {user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site-button">
              <Button variant="outline" size="sm" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
                <ExternalLink className="h-4 w-4" /> Lihat Website
              </Button>
            </a>
            <Button variant="outline" size="sm" onClick={onLogout} className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" /> Keluar
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        {!data ? (
          <p className="py-20 text-center font-mono text-sm text-slate-400">Memuat konten…</p>
        ) : (
          <Tabs defaultValue="profile" data-testid="admin-tabs">
            <TabsList className="mb-6 flex h-auto w-full flex-wrap justify-start gap-1 bg-white p-1.5 shadow-sm" data-testid="admin-tabs-list">
              {TABS.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} className="data-[state=active]:bg-navy data-[state=active]:text-white" data-testid={`admin-tab-${tab.value}`}>
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            <TabsContent value="profile">
              <SettingsForm settings={settings} schema={PROFILE_SCHEMA} refresh={refresh} testid="admin-settings-profile" />
            </TabsContent>
            <TabsContent value="summary">
              <SettingsForm settings={settings} schema={SUMMARY_SCHEMA} refresh={refresh} testid="admin-settings-summary" />
            </TabsContent>
            <TabsContent value="kpis">
              <ListEditor collection="kpis" items={data.kpis} schema={KPI_SCHEMA} titleKey={(f) => `${f.prefix || ""}${f.value}${f.suffix || ""} — ${f.label?.en || ""}`} blank={() => ({ value: 0, prefix: "", suffix: "", decimals: 0, label: { en: "", id: "" }, description: { en: "", id: "" } })} refresh={refresh} />
            </TabsContent>
            <TabsContent value="cases">
              <ListEditor collection="case_studies" items={data.case_studies} schema={CASE_SCHEMA} titleKey={(f) => f.brand} blank={() => ({ brand: "", industry: { en: "", id: "" }, channels: [], title: { en: "", id: "" }, challenge: { en: "", id: "" }, strategy: { en: "", id: "" }, execution: { en: "", id: "" }, result: { en: "", id: "" }, metrics: [], image_url: "" })} refresh={refresh} />
            </TabsContent>
            <TabsContent value="experience">
              <ListEditor collection="experiences" items={data.experiences} schema={EXP_SCHEMA} titleKey={(f) => `${f.role?.en || ""} — ${f.company || ""}`} blank={() => ({ role: { en: "", id: "" }, company: "", period: "", responsibilities: { en: "", id: "" }, achievements: { en: "", id: "" } })} refresh={refresh} />
            </TabsContent>
            <TabsContent value="skills">
              <ListEditor collection="skills" items={data.skills} schema={SKILL_SCHEMA} titleKey={(f) => f.title?.en || ""} blank={() => ({ title: { en: "", id: "" }, description: { en: "", id: "" }, items: [] })} refresh={refresh} />
            </TabsContent>
            <TabsContent value="certs">
              <ListEditor collection="certifications" items={data.certifications} schema={CERT_SCHEMA} titleKey={(f) => f.name?.en || ""} blank={() => ({ name: { en: "", id: "" }, issuer: "", year: "", credential_url: "" })} refresh={refresh} />
            </TabsContent>
            <TabsContent value="contact">
              <div className="space-y-6">
                <SettingsForm settings={settings} schema={CONTACT_SCHEMA} refresh={refresh} testid="admin-settings-contact" />
                <PasswordSection />
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
